import { Component, Vue } from 'vue-property-decorator';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { ConfigurationSectionTypeEnum } from '@/enums/ConfigurationSectionTypeEnums';
import { Configuration } from '@/models/configuration/Configuration';
import { ConfigurationField } from '@/models/configuration/Field';
import { ConfigurationSection } from '@/models/configuration/Section';
import { Helpers } from '@/helpers/Helpers';
import { DataSet } from '@/models/dataset/DataSet';
import { EventStore } from '@/store/store.event';
import { EventsService, SynchronizationService } from '@/services/index';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { AppSettingsStore } from '@/store/store.appSettings';
import { EditorModeEnums } from '@/enums/EditorModeEnums';
import { ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { DataSetEditor } from '@/models/dataset/DataSetEditor';
import { ConfigurationService } from '@/services/index';
import { QuickEditModel } from '@/models/quickEdit/QuickEdit';
import { QuickEditFieldModel } from '@/models/quickEdit/QuickEditField';
import { QuickEditService, AppSettingsService, AxDataService } from '@/services/index';

import { ISectionComponentMethods } from '@/components/edit/section/Section';
import Section from '@/components/edit/section/Section.vue';
import HeaderBar from '@/components/general/headerbar/HeaderBar.vue';

@Component({
    components: {
        HeaderBar,
        Section,
    },
})
export default class QuickEditComponent extends Vue {
    public epindex: number = 0;
    public epIndizes: number[] = [0];
    public isReadonly: boolean = true;
    public saveButtonActive: boolean = true;

    public dialog: boolean = false;
    public dialogHeadline: string = '';
    public dialogText: string = '';
    public dialogCancel: string = '';
    public dialogOk: string = '';
    public dialogCheckbox: boolean = false;
    public dialogCheckboxShow: boolean = false;
    public dialogCheckboxText: string = '';
    public closeForm: boolean = false;

    public creator?: DataSetEditor = new DataSetEditor();
    public editor?: DataSetEditor = new DataSetEditor();
    public showInfo: boolean = false;

    public quickCopyToast: boolean = false;
    public quickCopyToastTimeout: number = 3000;
    public quickCopyToastText: string = '';

    public quickEditConfigurationSection: ConfigurationSection[] = [];
    private samplesConfiguration?: ConfigurationSection;

    private quickEditFormId?: string;
    private wizardFormId?: string;
    private wizardSerialNumber?: string;

    // tslint:disable-next-line: no-empty
    public dialogCallbackOK = () => {};

    public async mounted() {
        this.quickEditFormId = this.$route.params.quickEditFormId || undefined;
        this.wizardFormId = this.$route.params.wizardFormId || undefined;
        this.wizardSerialNumber = this.$route.params.wizardSerialNumber || undefined;

        if (this.quickEditFormId != null) {
            const quickEditConfig = QuickEditService.GetQuickEditConfigurations.find((qvc) => qvc.formId === this.quickEditFormId);

            if (quickEditConfig != null) {
                // Clear used sections in storage
                QuickEditService.ClearUsedQuickEditConfigurationSections();
                QuickEditService.GenerateUsedQuickEditConfigurationSections(quickEditConfig.sections);
                this.quickEditConfigurationSection = this.getUsedQuickEditConfigurationSections;
                this.samplesConfiguration = this.getSamplesConfiguration;
                // create new dataSet
                AppSettingsStore.SetEditMode(EditorModeEnums.CREATE);

                this.openAllSections();
            } else {
                // TODO: Error message on wrong form id
            }
        } else if (this.wizardFormId != null) {
            const quickEditConfig = QuickEditService.quickEditConfigurationsFromWizard;
            if (quickEditConfig != null) {
                // Clear used sections in storage
                QuickEditService.ClearUsedQuickEditConfigurationSections();
                QuickEditService.GenerateUsedQuickEditConfigurationSections(quickEditConfig.sections);
                this.quickEditConfigurationSection = this.getUsedQuickEditConfigurationSections;
                this.samplesConfiguration = this.getSamplesConfiguration;
                // create new dataSet
                AppSettingsStore.SetEditMode(EditorModeEnums.CREATE);

                this.openAllSections();

                if (this.wizardSerialNumber != null && this.wizardSerialNumber.length > 0) {
                    LoggerService.debug(`wizardSerialNumber: `, this.wizardSerialNumber);
                    this.$nextTick(() => {
                        AxDataService.FilterCustomersBySerialnumber(this.wizardSerialNumber || '');
                    });
                } else {
                    LoggerService.debug(`no serialNubmer set?`, this.$route.params.wizardSerialNumber);
                }
            }
        } else {
            // TODO: Error message on wrong form id
        }
    }

    public beforeDestroy() {
        ConfigurationService.ClearUsedConfigurationSections();
        AxDataService.SetSelectedCustomer();
    }

    get getUsedQuickEditConfigurationSections() {
        return QuickEditService.usedQuickEditConfigurationSections;
    }

    get getSamplesConfiguration() {
        return QuickEditService.GetUsedQuickEditSamplesConfigurationSection;
    }

    public onOpenRequest(sectionIndex: number) {
        const openIndex = this.epIndizes.findIndex((x) => x === sectionIndex);

        LoggerService.LogDebug(`onOpenRequest: ${sectionIndex} | ${this.epindex}`);

        if (openIndex > -1) {
            if (this.checkOpenSection(sectionIndex) === true) {
                this.epIndizes.splice(openIndex, 1);
            }
        } else {
            this.epindex = sectionIndex;
            this.epIndizes.push(sectionIndex);
        }
    }

    public onClick(sectionIndex: number) {
        LoggerService.LogDebug(`Section ${this.quickEditConfigurationSection[sectionIndex].name} clicked: ${sectionIndex}`);
        this.onOpenRequest(sectionIndex);
    }

    public onSaveClicked(close: boolean) {
        this.closeForm = close;
        if (this.checkAllSectionComponents() === false) {
            return;
        } else {
            /**
             * Check materials extra, for the blend check
             */
            const sectionIndex = this.quickEditConfigurationSection.findIndex((sec) => sec.identifier === ApiIdentifers.material);
            if (sectionIndex > -1 && this.checkOpenSection(sectionIndex) === false) {
                return;
            } else if (this.checkAllSections() === true) {
                this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.save.title');
                this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.save.text');
                this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
                this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.save');
                this.dialogCheckboxText = this.$vuetify.lang.t('$vuetify.edit.dialog.save.pdf');
                this.dialogCheckboxShow = true;

                this.dialogCallbackOK = () => {
                    this.dialog = false;
                    this.dialogCheckboxShow = false;
                    this.SaveSections(this.dialogCheckbox);
                };

                this.dialog = true;
            }
        }
    }

    public onCancelClicked() {
        this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.title');
        this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.text');
        this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
        this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.ok');

        this.dialogCallbackOK = () => {
            this.dialog = false;
            this.$router.back();
        };

        this.dialog = true;
    }

    /**
     * Method to fetch translation with fallback
     * @param section ConfigurationSection
     */
    public getIdentifierText(section: ConfigurationSection): string {
        let ret: string = '';
        ret = this.$vuetify.lang.t(`$vuetify.identifiers.${section.identifier}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === section.identifier || ret.includes('$vuetify.identifiers.')) {
            ret = section.name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${section.identifier} --> ${ret}`);
        }
        return ret;
    }

    private removeInnerSubSectionFields(allFields: ConfigurationField[] | undefined, fieldsToAdd: QuickEditFieldModel[] | undefined, parentIdentifier: string) {
        if (allFields && fieldsToAdd) {
            const fieldsToRemove: ConfigurationField[] = [];
            allFields.forEach((field) => {
                let fieldIndex = fieldsToAdd.findIndex((f) => f.identifier === field.identifier);
                if (fieldIndex !== -1) {
                    this.removeInnerSubSectionFields(field.fields, fieldsToAdd[fieldIndex].fields, fieldsToAdd[fieldIndex].identifier);
                } else {
                    fieldIndex = allFields.findIndex((f) => f.identifier.includes(parentIdentifier));
                    if (fieldIndex !== -1) {
                        // for dynamic created sections with identifier + __count
                        this.removeInnerSubSectionFields(allFields[fieldIndex].fields, fieldsToAdd, parentIdentifier);
                    } else {
                        fieldsToRemove.push(field);
                    }
                }
            });
            for (let i = fieldsToRemove.length - 1; i >= 0; --i) {
                const index = allFields.findIndex((t) => t.identifier === fieldsToRemove[i].identifier);
                if (index !== -1) {
                    allFields.splice(index, 1);
                }
            }
        }
    }

    private getSectionRef(sectionRefName: string): ISectionComponentMethods & Section {
        return Helpers.getReference<ISectionComponentMethods & Section>(this.$refs, sectionRefName);
    }

    private async SaveSections(generatePdf: boolean) {
        // sort machines to have correct order in save data
        this.quickEditConfigurationSection.sort((lhs, rhs) => lhs.position - rhs.position);
        // sort sample sections
        this.sortSamples(this.quickEditConfigurationSection);
        const editDataSet = new DataSet({ sections: this.quickEditConfigurationSection });

        const eventType = DataSetEventTypeEnums.CREATE;
        await EventsService.PutUnsyncedEvent(
            new DexieDataSetEvent({
                dataSet: editDataSet,
                event: eventType,
                date: new Date().toISOString(),
            }),
        )
            .then(async (data: number) => {
                if (LoggerService.defaultLogLevel === LoggerTypeEnum.DEBUG) {
                    await EventsService.GetAllUnsyncedEvents().then((response: DexieDataSetEvent[]) => {
                        LoggerService.LogDebug(`GetAllUNsyncedEvents: `, response);
                    });
                }
                LoggerService.LogDebug(`Saved section as local unsynced dataSet number: ${data}`);
                await EventStore.SetUnsyncedEvents();

                if (generatePdf) {
                    /**
                     * If user wants to generate a pdf sync is done later.
                     */
                    this.$router.replace({ name: 'settingsReport', params: { unsyncedEventId: data.toString(), quickEdit: 'true' } });
                } else {
                    if (SynchronizationService.IsRunnung() === true) {
                        /**
                         * only sync now if auto sync is running
                         * this only needed for debug mode so we can
                         * see unsynced events better even if online
                         */
                        SynchronizationService.SyncNow();
                    }
                    AppSettingsService.SetNavigateTo = 'table';
                    this.$router.back();
                }
            })
            .catch((error: any) => {
                LoggerService.LogError(`Error on saving: `, error);
            });
    }

    private checkOpenSection(index?: number): boolean {
        const sectionRef = this.getSectionRef(`section_${index != null ? index : this.epindex}`);
        return sectionRef.validateSection();
    }

    private checkAllSectionComponents(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        for (let index = 0; index < this.quickEditConfigurationSection.length; index++) {
            const sectionRef = this.getSectionRef(`section_${index}`);
            const valid = sectionRef.validateSection();
            if (valid === false) {
                this.epindex = index;
                invalidSections.push(index);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }
        return allSectionsValid;
    }

    private checkAllSections(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        const sectionCount = this.quickEditConfigurationSection.length;
        // tslint:disable-next-line: prefer-for-of
        for (let sectionIndex = 0; sectionIndex < sectionCount; sectionIndex++) {
            const section = this.quickEditConfigurationSection[sectionIndex];
            const ret: boolean = section.isValidSection();
            allSectionsValid = ret;
            if (allSectionsValid === false) {
                this.epindex = sectionIndex;
                invalidSections.push(sectionIndex);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }

        return allSectionsValid;
    }

    private sortSamples(inSections: ConfigurationSection[]) {
        const samples = inSections.find((sec) => sec.identifier === ApiIdentifers.sample);
        if (samples !== undefined && samples.fields.length > 0) {
            const sortOrder = this.findSectionField(samples.fields[0], ApiIdentifers.sampleLaboratoryExtractionPoint);
            if (sortOrder !== undefined) {
                samples.fields.sort((lhs, rhs) => {
                    const lhsExPoint = this.findSectionField(lhs, ApiIdentifers.sampleLaboratoryExtractionPoint);
                    const rhsExPoint = this.findSectionField(rhs, ApiIdentifers.sampleLaboratoryExtractionPoint);
                    if (lhsExPoint && rhsExPoint) {
                        return sortOrder.values!.findIndex((item) => item.value === lhsExPoint.valueNumber) - sortOrder.values!.findIndex((item) => item.value === rhsExPoint.valueNumber);
                    }
                    return 0;
                });

                for (let index = 0; index < samples.fields.length; index++) {
                    const sample = samples.fields[index];
                    sample.position = index;
                    sample.identifier = sample.identifier.replace(/__[0-9]+$/, `__${index}`);
                }
            }
        }
    }

    private findSectionField(inField: ConfigurationField, inIdentififer: string): ConfigurationField | undefined {
        let searchField: ConfigurationField | undefined;
        if (inField.identifier === inIdentififer) {
            searchField = inField;
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subSearchField = this.findSectionField(subField, inIdentififer);
                if (subSearchField !== undefined) {
                    searchField = subSearchField;
                    break;
                }
            }
        }
        return searchField;
    }

    private openAllSections() {
        let i = 0;
        this.epIndizes = Array.from(new Array(this.quickEditConfigurationSection.length), () => i++);
    }
}
